/* eslint-disable react/no-unknown-property */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { numberFormatter, numbersDeclension } from 'utils/formatters';
import { useAppSelector } from 'hooks';
import classNames from 'classnames';
import { LimitedVMParam } from 'store/virtual-machine/types/state.types';
import useDynamicTranslation from 'hooks/use-dynamic-translation';
import Badge from 'components/UI_kit_3/Badge/Badge';
import styles from './ResourcesLimits.module.scss';

interface IResourcesLimitProps extends React.HTMLProps<HTMLDivElement> {
  limitsToShow: LimitedVMParam[]
}

const ResourcesLimit: React.FC<IResourcesLimitProps> = ({ className, limitsToShow, ...props }) => {
  const [t] = useTranslation();
  const limits = useAppSelector((state) => state.virtualMachine.paramLimits);
  const getDynamicTranslation = useDynamicTranslation();

  return (
    <section className={classNames(styles.resourcesLimits, className)} {...props}>
      {/* <div className={styles.resourcesLimitsContainer}> */}
      <div className={styles.accessibleResources}>{`${t('vm.accessible_resources')}:`}</div>
      {limitsToShow.includes(LimitedVMParam.CPUCores) && (
        <Badge pill size="sm" className={styles.resourceItem} qaElement="resource-cpu">
          <span>{`${t('vm.params.cpu')}: `}</span>
          <span qa-element="cpu-limit">
            {`${numberFormatter(limits.cpu_cores?.limit)} `}
          </span>
          <span>
            {numbersDeclension(
              limits.cpu_cores?.limit || 0,
              [t('vm.params.core.1'), t('vm.params.core.2'), t('vm.params.core.5')],
            )}
          </span>
        </Badge>
      )}
      {limitsToShow.includes(LimitedVMParam.RAM) && (
        <Badge pill size="sm" className={styles.resourceItem} qaElement="resource-ram">
          <span>{`${t('vm.params.ram_2')}: `}</span>
          <span qa-element="ram-limit">
            {`${numberFormatter(limits.ram?.limit)} `}
          </span>
          <span>{t('common.gb')}</span>
        </Badge>
      )}
      {limitsToShow.includes(LimitedVMParam.HDDSize) && (
        <Badge pill size="sm" className={styles.resourceItem} qaElement="resource-hdd">
          <span>{`${t('disk.disk_types.hdd_long')}: `}</span>
          <span qa-element="hdd-size-limit">
            {`${numberFormatter(limits.hdd_size?.limit)} `}
          </span>
          <span>{t('common.gb')}</span>
        </Badge>
      )}
      {limitsToShow.includes(LimitedVMParam.SSDSize) && (
        <Badge pill size="sm" className={styles.resourceItem} qaElement="resource-ssd">
          <span>{`${t('disk.disk_types.ssd_long')}: `}</span>
          <span qa-element="ssd-size-limit">
            {`${numberFormatter(limits.ssd_size?.limit)} `}
          </span>
          <span>{t('common.gb')}</span>
        </Badge>
      )}
      {(limitsToShow.includes(LimitedVMParam.NVMESize) && limits.nvme_size) && (
        <Badge pill size="sm" className={styles.resourceItem} qaElement="resource-nvme">
          <span>{`${getDynamicTranslation('disk.disk_types.nvme_long')}: `}</span>
          <span qa-element="nvme-size-limit">
            {`${numberFormatter(limits.nvme_size?.limit)} `}
          </span>
          <span>{t('common.gb')}</span>
        </Badge>
      )}
      {limitsToShow.includes(LimitedVMParam.PublicIP) && (
        <Badge pill size="sm" className={styles.resourceItem} qaElement="resource-public-ip">
          <span>
            {t('vm.params.external_ip')}
            {': '}
          </span>
          <span qa-element="public-ip-limit">
            {`${numberFormatter(limits.public_ip?.limit)}`}
          </span>
        </Badge>
      )}
      {limitsToShow.includes(LimitedVMParam.LocalNetworks) && (
        <Badge pill size="sm" className={styles.resourceItem} qaElement="resource-local-network">
          <span>
            {t('network.local_network')}
            {': '}
          </span>
          <span qa-element="local-network-limit">
            {`${numberFormatter(limits.vlan?.limit)}`}
          </span>
        </Badge>
      )}

      {limitsToShow.includes(LimitedVMParam.BackupQuota) && (
        <Badge pill size="sm" className={styles.resourceItem} qaElement="resource-backup">
          <span>
            {t('backup.self_plural')}
            {': '}
          </span>
          <span qa-element="backup-limit">
            {`${numberFormatter(limits.backup_quota?.limit)}`}
          </span>
        </Badge>
      )}
      {limitsToShow.includes(LimitedVMParam.CustomTemplates) && (
        <Badge pill size="sm" className={styles.resourceItem} qaElement="resource-custom-templates">
          <span>
            {t('report.resources.custom_image')}
            {': '}
          </span>
          <span qa-element="custom-templete-limit">
            {`${numberFormatter(limits.custom_image?.limit)}`}
          </span>
        </Badge>
      )}
      {limitsToShow.includes(LimitedVMParam.Storage) && (
        <Badge pill size="sm" className={styles.resourceItem} qaElement="resource-storage">
          <span>
            {t('report.resources.s3_storage')}
            {': '}
          </span>
          <span qa-element="s3-limit">
            {`${parseFloat(((limits.s3_storage?.limit || 0) / 1024).toFixed(1))} `}
          </span>
          <span>{t('common.gb')}</span>
        </Badge>

      )}
      {limitsToShow.includes(LimitedVMParam.VmUserTemplate) && (
        <Badge pill size="sm" className={styles.resourceItem} qaElement="resource-vm-user-template">
          <span>
            {t('report.resource_component.vm_template')}
            {': '}
          </span>
          <span qa-element="vm-user-template-limit">
            {`${limits.vm_template?.limit || 0} `}
          </span>
          <span>{t('common.gb')}</span>
        </Badge>
      )}
      {limitsToShow.includes(LimitedVMParam.DatabaseCpu) && (
        <Badge pill size="sm" className={styles.resourceItem} qaElement="resource-vm-database-cpu">
          <span>
            {t(`report.resource_component.${LimitedVMParam.DatabaseCpu}`)}
            {': '}
          </span>
          <span qa-element="database-cpu-limit">
            {`${limits.managed_db_ram?.limit || 0} `}
          </span>
          <span>
            {numbersDeclension(
              limits.managed_db_cpu?.limit || 0,
              [t('vm.params.core.1'), t('vm.params.core.2'), t('vm.params.core.5')],
            )}
          </span>
        </Badge>
      )}
      {limitsToShow.includes(LimitedVMParam.DatabaseRam) && (
        <Badge pill size="sm" className={styles.resourceItem} qaElement="resource-database-ram">
          <span>
            {t(`report.resource_component.${LimitedVMParam.DatabaseRam}`)}
            {': '}
          </span>
          <span qa-element="database-ram-limit">
            {`${limits.managed_db_ram?.limit || 0} `}
          </span>
          <span>{t('common.gb')}</span>
        </Badge>
      )}
      {limitsToShow.includes(LimitedVMParam.DatabaseDisk) && (
        <Badge pill size="sm" className={styles.resourceItem} qaElement="resource-database-disk">
          <span>
            {t(`report.resource_component.${LimitedVMParam.DatabaseDisk}`)}
            {': '}
          </span>
          <span qa-element="database-disk-limit">
            {`${limits.managed_db_disk?.limit || 0} `}
          </span>
          <span>{t('common.gb')}</span>
        </Badge>
      )}
    </section>
  );
};

export default ResourcesLimit;
